/**
 * Editar o componente ou o conector **sem sair do gráfico**.
 *
 * Este arquivo é curto porque a peça de verdade já existia: o
 * `PainelDeCadastro` foi feito solto exatamente para isto — *"tem que ser um
 * componente à parte, pra que ele possa ser utilizado dentro dos outros
 * sistemas"*. Aqui ele é hospedado pela tela do gráfico em vez da de Cadastros.
 *
 * **Por que não navegar.** O desenho leva até 18 segundos para sair e não é
 * reaproveitado, de propósito. Sair da tela para trocar uma descrição e voltar
 * custa esses 18 segundos de novo — e, no meio do caminho, a referência do que
 * se estava olhando. Guardar o desenho para o retorno seria o outro caminho,
 * mas aí eu teria de decidir sozinho quando ele ficou velho, que é justamente a
 * decisão de não ter cache.
 */

import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { usarExcluir, usarSalvar, type ItemDeCadastro } from "@/api/cadastros"
import { ESQUEMAS, esteEsta, type EntidadeCadastro } from "@/telas/cadastros/esquemas"
import { PainelDeCadastro, type ResultadoDeSalvar } from "@/telas/cadastros/PainelDeCadastro"

export type AlvoDeEdicao = { entidade: EntidadeCadastro; id: number }

export function GavetaDeCadastro({
  projetoId, alvo, contexto, aoFechar, aoSalvou,
}: {
  projetoId: number
  alvo: AlvoDeEdicao
  /** Linha de contexto no cabeçalho — de quem é este registro. */
  contexto?: string
  aoFechar: () => void
  /** Avisa a tela que o desenho na tela virou passado. */
  aoSalvou: () => void
}) {
  const esquema = ESQUEMAS[alvo.entidade]

  const registro = useQuery({
    queryKey: [alvo.entidade, alvo.id],
    queryFn: () => obter<ItemDeCadastro>(`${esquema.rota}/${alvo.id}`, {}),
  })

  const salvar = usarSalvar(esquema.rota, projetoId)
  const excluir = usarExcluir(esquema.rota, projetoId)

  async function gravar(dados: Record<string, unknown>): Promise<ResultadoDeSalvar> {
    try {
      await salvar.mutateAsync({ id: alvo.id, dados })
      await registro.refetch()
      aoSalvou()
      return { ok: true, id: alvo.id }
    } catch (e) {
      return { ok: false, erro: e }
    }
  }

  return (
    <aside
      className="gaveta"
      aria-label={`Editar ${esquema.singular}`}
      style={{
        flex: "0 0 380px", width: 380, borderLeft: "1px solid var(--line2)",
        background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
      }}
    >
      {registro.isPending ? (
        <p style={{ margin: 0, padding: 16, fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
      ) : registro.error || !registro.data ? (
        <p style={{ margin: 0, padding: 16, fontSize: 12.5, color: "var(--erro)" }}>
          Não foi possível abrir {esteEsta(esquema)} {esquema.singular}.
        </p>
      ) : (
        <PainelDeCadastro
          esquema={esquema}
          projetoId={projetoId}
          registro={registro.data as unknown as Record<string, unknown>}
          acima={contexto}
          salvando={salvar.isPending}
          aoSalvar={gravar}
          aoCancelar={aoFechar}
          // Sem excluir aqui de propósito: apagar o componente que está
          // desenhado deixaria a tela mostrando uma coisa que não existe mais.
          // Exclusão continua em Cadastros, onde a lista some junto.
          excluindo={excluir.isPending}
        />
      )}
    </aside>
  )
}
